import {
  createSlice,
  Draft,
  PayloadAction
} from "@reduxjs/toolkit";
import {
  TestCase,
  TestCaseTemplate,
  TransformTestCase
} from "../../types/testCaseTemplateTypes";
import { TransformExecuteResponse } from "../../types/clientTypes";
import { getTransformTestCase } from "../../utils/testCaseUtils";
import { SelectedVariable } from "../../types/variableType";
import { setSelectedVariables } from "./mainSlice";

type State = {
  selectedTestCase: TransformTestCase,
  selectedTestCaseTemplate: TestCaseTemplate,
  executeResponse: TransformExecuteResponse
}

const templateInitialState: State = {
  selectedTestCase: {
    name: "",
    testCaseTemplateId: 0,
    responseParameterValues: [],
    requestParameterValues: []
  },
  selectedTestCaseTemplate: {
    id: 0,
    name: "",
    methodId: 0,
    statusCodeId: 0,
    sectionId: 0,
    maximumExecutionTimeout: 0,
    description: "",
    endpoint: "",
    payload: "",
    body: "",
    expectedBody: "",
    testCaseTemplateFolderId: 0,
    isPositive: false,
    priorityId: 0,
    isJsonSchemaValidation: false,
    childTestCases: [],
    requestHeaders: [],
    responseParameters: [],
    requestParameters: [],
    environmentServers: []
  },
  executeResponse: {
    responseBody: "",
    time: 0,
    statusCode: 0,
    isError: false,
  }
}

const testCaseTemplateSlice = createSlice({
  name: "testCaseSlice",
  initialState: templateInitialState,
  reducers: {
    setSelectedTestCase: (state, action: PayloadAction<{ testCase: TestCase, template: TestCaseTemplate, selectedVariables?: Array<SelectedVariable> }>) => {
      const template = action.payload.template;
      const testCase = action.payload.testCase;
      if (!!action.payload.selectedVariables) {
        state.selectedTestCase = getTransformTestCase(action.payload.selectedVariables, template, testCase);
      } else {
        state.selectedTestCase = testCase;
      }
      state.selectedTestCaseTemplate = template;
    },
    setSelectedTestCaseTemplate: (state, action: PayloadAction<TestCaseTemplate>) => {
      state.selectedTestCaseTemplate = action.payload;
    },
    setExecuteResponse: (state, action: PayloadAction<TransformExecuteResponse>) => {
      state.executeResponse = action.payload;
    },
    deleteExecuteResponse: (state) => {
      deleteExecuteResponseLogic(state)
    },
    deleteSelectedTestCase: (state) => {
      deleteSelectedTestCaseLogic(state);
    },
    deleteSelectedTestCaseTemplate: (state) => {
      deleteSelectedTemplateLogic(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSelectedVariables, (state, action) => {
      if (!!state.selectedTestCase.id) {
        state.selectedTestCase = getTransformTestCase(action.payload, state.selectedTestCaseTemplate, state.selectedTestCase);
      }
      if (state.executeResponse.statusCode !== 0) {
        deleteExecuteResponseLogic(state);
      }
    });
  }
});

const deleteSelectedTemplateLogic = (state: Draft<State>) => {
  state.selectedTestCaseTemplate = {
    id: 0,
    name: "",
    methodId: 0,
    statusCodeId: 0,
    sectionId: 0,
    maximumExecutionTimeout: 0,
    description: "",
    endpoint: "",
    payload: "",
    body: "",
    expectedBody: "",
    testCaseTemplateFolderId: 0,
    isPositive: false,
    priorityId: 0,
    isJsonSchemaValidation: false,
    childTestCases: [],
    requestHeaders: [],
    requestParameters: [],
    responseParameters: [],
    environmentServers: []
  };
  deleteSelectedTestCaseLogic(state);
}

const deleteSelectedTestCaseLogic = (state: Draft<State>) => {
  state.selectedTestCase = {
    name: "",
    id: 0,
    testCaseTemplateId: 0,
    responseParameterValues: [],
    requestParameterValues: []
  }
  deleteExecuteResponseLogic(state);
}

const deleteExecuteResponseLogic = (state: Draft<State>) => {
  state.executeResponse = {
    responseBody: "",
    time: 0,
    statusCode: 0,
    isError: false,
    isValid: false,
    validationErrorMessage: ""
  };
}

export const {
  setSelectedTestCase,
  setSelectedTestCaseTemplate,
  setExecuteResponse,
  deleteExecuteResponse,
  deleteSelectedTestCase,
  deleteSelectedTestCaseTemplate
} = testCaseTemplateSlice.actions;

export default testCaseTemplateSlice.reducer;